import { requestJson } from "./http";

export async function fetchMyTimecards() {
  const data = await requestJson("/api/me/timecards");
  return Array.isArray(data) ? data : [];
}

export async function fetchTimecard(timecardId, signal) {
  try {
    return await requestJson(`/api/timecards/${timecardId}`, { signal });
  } catch (error) {
    if (error?.status === 404) {
      return null;
    }

    throw error;
  }
}

export async function saveTimecard(timecard) {
  const isNew = !timecard.timecardId || timecard.timecardId === 0;
  const url = isNew ? "/api/timecards" : `/api/timecards/${timecard.timecardId}`;
  const method = isNew ? "POST" : "PUT";

  return requestJson(url, {
    method,
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(timecard)
  });
}

export async function deleteTimecard(timecardId) {
  await requestJson(`/api/timecards/${timecardId}`, {
    method: "DELETE"
  });
}

export async function submitTimecard(timecardId) {
  return requestJson(`/api/timecards/${timecardId}/submit`, {
    method: "POST"
  });
}

export async function resubmitTimecard(timecardId, reason) {
  return requestJson(`/api/timecards/${timecardId}/resubmit`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ reason })
  });
}

export async function fetchResubmissions(timecardId) {
  const data = await requestJson(`/api/timecards/${timecardId}/resubmissions`);
  return Array.isArray(data) ? data : [];
}

function parseDownloadFileName(contentDisposition) {
  if (!contentDisposition) {
    return "";
  }

  const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]).trim();
  }

  const fileNameMatch = contentDisposition.match(/filename\s*=\s*"?([^\";]+)"?/i);
  return fileNameMatch?.[1]?.trim() ?? "";
}

export async function downloadHospitalCombinedExport(hospitalId, weekStartDate) {
  const response = await fetch(`/api/admin/hospitals/${hospitalId}/pdf?weekStartDate=${weekStartDate}`, {
    credentials: "same-origin"
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    let message = `Request failed with status ${response.status}.`;

    if (contentType.includes("application/json")) {
      const payload = await response.json();
      message = payload?.message || message;
    } else {
      const text = await response.text();
      message = text || message;
    }

    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  const contentType = response.headers.get("content-type") ?? "application/octet-stream";
  const fileName =
    parseDownloadFileName(response.headers.get("content-disposition")) ||
    `Hospital_${hospitalId}_Combined.pdf`;

  return {
    blob: await response.blob(),
    contentType,
    fileName
  };
}

export async function downloadTimecardExport(timecardId) {
  const response = await fetch(`/api/timecards/${timecardId}/pdf`, {
    credentials: "same-origin"
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    let message = `Request failed with status ${response.status}.`;

    if (contentType.includes("application/json")) {
      const payload = await response.json();
      message = payload?.message || message;
    } else {
      const text = await response.text();
      message = text || message;
    }

    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  const contentType = response.headers.get("content-type") ?? "application/octet-stream";
  const fileName =
    parseDownloadFileName(response.headers.get("content-disposition")) ||
    (contentType.includes("zip")
      ? `Timecard_${timecardId}_ByHospital.zip`
      : `Timecard_${timecardId}.pdf`);

  return {
    blob: await response.blob(),
    contentType,
    fileName
  };
}
